import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { onAuthStateChanged, signInWithPopup, signOut } from 'firebase/auth';
import { ref, get, set, onValue } from 'firebase/database';
import { auth, googleProvider, db } from './firebase';
import Sidebar from './components/Sidebar';
import DailyReport from './pages/DailyReport';
import Analysis from './pages/Analysis';
import Settings from './pages/Settings';
import FinanceSimulator from './pages/FinanceSimulator';
import Playground from './pages/Playground';
import AdminLayout from './admin/AdminLayout';
import AdminWatchlists from './pages/AdminWatchlists';
import AdminConfigurations from './pages/AdminConfigurations';
import AdminUsers from './pages/AdminUsers';
import './App.css';

const SYSTEM_MAINTENANCE_REF = 'TSS-Config/system_maintenance';
const SIDEBAR_STORAGE_KEY = 'tradeviser-sidebar-collapsed';

function MaintenanceGate({ maintenanceMode, children }) {
  const location = useLocation();
  if (maintenanceMode && !location.pathname.startsWith('/admin')) {
    return (
      <div className="app-maintenance" role="alert">
        <h2 className="app-maintenance-title">System maintenance</h2>
        <p className="app-maintenance-message">We are currently performing maintenance. Please try again later.</p>
      </div>
    );
  }
  return children;
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [authorized, setAuthorized] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(SIDEBAR_STORAGE_KEY) ?? 'false');
    } catch {
      return false;
    }
  });

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setAuthorized(null);
      setLoading(false);
      setError(null);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!user) {
      setAuthorized(null);
      setIsAdmin(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const [allowedSnap, adminSnap] = await Promise.all([
          get(ref(db, `AllowedUsers/${user.uid}`)),
          get(ref(db, `Admins/${user.uid}`)),
        ]);
        if (!cancelled) {
          const isAllowed = allowedSnap.exists();
          const isAdmin = adminSnap.exists();
          setAuthorized(isAllowed || isAdmin);
          setIsAdmin(isAdmin);
        }
        const allowedSnapExists = allowedSnap.exists();
        const adminSnapExists = adminSnap.exists();
        if (!allowedSnapExists && !adminSnapExists) {
          try {
            const pendingRef = ref(db, `PendingUsers/${user.uid}`);
            await set(pendingRef, {
              name: user.displayName ?? '',
              email: user.email ?? '',
            });
          } catch (pendingErr) {
            console.warn('Could not write PendingUsers (check RTDB rules allow write to PendingUsers/<uid>):', pendingErr);
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Failed to verify access');
          setAuthorized(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  useEffect(() => {
    if (!user || authorized !== true) {
      setMaintenanceMode(false);
      return;
    }
    const maintenanceRef = ref(db, SYSTEM_MAINTENANCE_REF);
    const unsubscribe = onValue(maintenanceRef, (snap) => {
      setMaintenanceMode(snap.val() === true);
    });
    return unsubscribe;
  }, [user, authorized]);

  const handleGoogleSignIn = async () => {
    setError(null);
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (err) {
      setError(err.message || 'Sign in failed');
    }
  };

  const handleSignOut = async () => {
    setError(null);
    try {
      await signOut(auth);
    } catch (err) {
      setError(err.message || 'Sign out failed');
    }
  };

  const checkingAccess = user && authorized === null;

  if (loading || checkingAccess) {
    return (
      <div className="app-loading-screen" role="status" aria-live="polite">
        <div className="spinner" aria-label="Loading" />
        <p>Loading…</p>
      </div>
    );
  }

  if (user && authorized === false) {
    return (
      <div className="App">
        <div className="app-unauthorized">
          <div className="app-unauthorized-card">
            <h1 className="app-unauthorized-title">Access denied</h1>
            <p className="app-unauthorized-message">
              Your account is not authorized to use this app. Please sign out and use an authorized account.
            </p>
            {error && (
              <p className="app-unauthorized-error" role="alert">{error}</p>
            )}
            <button type="button" className="btn btn-outline" onClick={handleSignOut}>
              Sign out
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <div className="App">
        <header className="app-header">
          <h1 className="app-title">Tradeviser</h1>
          {user ? (
            <div className="user-section">
              <div className="user-info">
                {user.photoURL && (
                  <img
                    src={user.photoURL}
                    alt=""
                    className="user-avatar"
                    width={32}
                    height={32}
                  />
                )}
                <span className="user-name">
                  {(() => {
                    const name = user.displayName || user.email || '';
                    if (!name || name.includes('@')) return name;
                    const words = name.trim().split(/\s+/).filter(Boolean);
                    const half = Math.max(1, Math.ceil(words.length / 2));
                    return words.slice(0, half).join(' ');
                  })()}
                </span>
              </div>
            </div>
          ) : (
            <button type="button" className="btn btn-primary" onClick={handleGoogleSignIn}>
              Sign in with Google
            </button>
          )}
        </header>

        {error && (
          <div className="app-error" role="alert">
            {error}
          </div>
        )}

        {user ? (
          <div className={`app-layout ${sidebarCollapsed ? 'app-layout--sidebar-collapsed' : ''}`}>
            <Sidebar
              isAdmin={isAdmin}
              collapsed={sidebarCollapsed}
              onCollapsedChange={(next) => {
                try {
                  localStorage.setItem(SIDEBAR_STORAGE_KEY, JSON.stringify(next));
                } catch {}
                setSidebarCollapsed(next);
              }}
            />
            <main className="app-main">
              <MaintenanceGate maintenanceMode={maintenanceMode}>
                <Routes>
                  <Route path="/" element={<Navigate to="/analysis" replace />} />
                  <Route path="/daily-report" element={<DailyReport isAdmin={isAdmin} user={user} />} />
                  <Route path="/analysis" element={<Analysis user={user} isAdmin={isAdmin} />} />
                  <Route path="/playground" element={<Playground />} />
                  <Route path="/watchlist" element={<Navigate to="/admin/watchlists" replace />} />
                  <Route path="/finance-simulator" element={<FinanceSimulator user={user} />} />
                  <Route path="/settings" element={<Settings user={user} onSignOut={handleSignOut} />} />
                  <Route path="/admin" element={<AdminLayout user={user} />}>
                    <Route index element={<Navigate to="watchlists" replace />} />
                    <Route path="watchlists" element={<AdminWatchlists isAdmin />} />
                    <Route path="configurations" element={<AdminConfigurations />} />
                    <Route path="users" element={<AdminUsers user={user} />} />
                  </Route>
                </Routes>
              </MaintenanceGate>
            </main>
          </div>
        ) : (
          <main className="app-main">
            <div className="app-center">
              <div className="login-hero">
                <div className="login-hero-glow" aria-hidden />
                <h2 className="login-hero-title">Stock analysis,<br />simplified.</h2>
                <p className="login-hero-desc">Real-time screening, daily reports, and actionable insights — all in one place.</p>
                <button type="button" className="btn btn-primary login-hero-btn" onClick={handleGoogleSignIn}>
                  Sign in with Google
                </button>
              </div>
            </div>
          </main>
        )}
      </div>
    </BrowserRouter>
  );
}

export default App;
